import { useAutoAnimate } from "@formkit/auto-animate/react";
import React from "react";
import { useEffect, useMemo, useState } from "react";

import { toast } from "@performyard/py-components";
import { ButtonStandard } from "@performyard/py-components";
import { Text as Typography } from "@performyard/py-components";

import { Grid, GridCol } from "../../../../../../components/Grid";
import SelectInput from "../../../../../../components/Inputs/SelectInput";
import { Checkboxes } from "../../../../../../components/Inputs/SelectionOptions/Checkboxes";
import FormModal from "../../../../../../components/Modal/Templates/FormModal";
import Spacer from "../../../../../../components/Spacer";
import { DateWithErrors } from "./DateWithErrors";

import {
	useSurveyAccess,
	useSetters as useSurveyParticipantsSetters,
} from "../../pages/SurveyParticipants/store/store";
import { useSurveyEmailStatus } from "../../pages/SurveyResults/hooks/useSurveyEmailStatus";

import { router } from "../../../../../../app/router";

import { SURVEY_DETAILS_MODALS } from "../../store/model";
import {
	useActions,
	useEndDate,
	useModal,
	usePublishFormErrors,
	useSetters,
	useStartDate,
	useSurveyStatus,
} from "../../store/store";

import { useReScheduleSurveyEmail, useUpdateSurvey } from "../../../../../../api/hooks";
import useSearchQueryParams from "../../../../../../hooks/useSearchQueryParams";
import { searchParamsSchema } from "../../hooks/useCompareDraftToSaved";
import { useSurvey } from "../../hooks/useSurvey";

import { formatErrorMessage } from "../../../../../../utils/TanstackQueryErrorHandling";

export const SurveySettingsModal = ({ surveyId }: { surveyId: string }) => {
	const { survey } = useSurvey({ surveyId });
	const modal = useModal();
	const setters = useSetters();
	const startDate = useStartDate();
	const endDate = useEndDate();
	const surveyStatus = useSurveyStatus();
	const publishFormErrors = usePublishFormErrors();
	const surveyAccess = useSurveyAccess();
	const surveyParticipantsSetters = useSurveyParticipantsSetters();
	const actions = useActions();
	const [scheduleDates, setScheduleDates] = useState<boolean>(false);
	const [originalValues, setOriginalValues] = useState<{
		startDate: Date | string;
		endDate: Date | string;
		scheduleDates: boolean;
		surveyAccess: "all-employees" | "selected-employees";
	} | null>(null);
	const [selectedSurveyAccess, setSelectedSurveyAccess] = useState<"all-employees" | "selected-employees">(
		"all-employees"
	);
	const { queryParams: searchParams } = useSearchQueryParams({
		schema: searchParamsSchema,
	});
	const tab = searchParams.tab;

	const { emailScheduled, scheduledTime, autoflowId, scheduledInTheFuture } = useSurveyEmailStatus(surveyId);
	const { mutate: reScheduleSurvey } = useReScheduleSurveyEmail();

	const reScheduleRequired = useMemo(() => {
		return (
			startDate &&
			emailScheduled &&
			autoflowId &&
			scheduledInTheFuture &&
			scheduledTime &&
			scheduledTime.toISOString() !== new Date(startDate).toISOString()
		);
	}, [emailScheduled, autoflowId, scheduledInTheFuture, scheduledTime, startDate]);

	const onSuccess = () => {
		// if the email has been scheduled and the start date has changed, reschedule the email
		if (reScheduleRequired) {
			reScheduleSurvey({
				surveyId: surveyId,
			});
		}
		if (hasSurveyAccessChanges) {
			handleSurveyAccessChange();
		}
		handleClose();
		toast.success("Your changes have been saved");
	};
	const onError = (error, mutation) => {
		//replicate default error handling (turned off for this mutation)
		const messages = formatErrorMessage(error, mutation);

		messages.forEach((message) => {
			toast.error(message);
		});
		handleClose();
	};
	const { mutate: publishSurvey } = useUpdateSurvey(surveyId, { onSuccess, onError });

	useEffect(() => {
		if (modal === SURVEY_DETAILS_MODALS.SURVEY_SETTINGS) {
			const initialStartDate = survey?.startDate ? new Date(survey.startDate) : "";
			const initialEndDate = survey?.endDate ? new Date(survey.endDate) : "";
			const initialScheduleDates = survey?.scheduleDates ?? !!survey?.startDate;
			const initialSurveyAccess = survey?.participantSelection?.enabled ? "selected-employees" : "all-employees";
			setSelectedSurveyAccess(initialSurveyAccess);
			surveyParticipantsSetters.setSurveyAccess(initialSurveyAccess);

			// Set the original values when modal opens
			setOriginalValues({
				startDate: initialStartDate,
				endDate: initialEndDate,
				scheduleDates: initialScheduleDates,
				surveyAccess: initialSurveyAccess,
			});

			// Set the current form values
			setters.setStartDate(initialStartDate);
			setters.setEndDate(initialEndDate);
			setScheduleDates(initialScheduleDates);
		}
	}, [modal, survey?.startDate, survey?.endDate, survey?.scheduleDates, survey?.participantSelection?.enabled]);

	const handleSurveyAccessChange = () => {
		if (surveyAccess === selectedSurveyAccess) return;
		if (selectedSurveyAccess === "selected-employees") {
			surveyParticipantsSetters.setEditParticipants(true);
			if (tab !== "participants") {
				router.navigate(`/surveys/${surveyId}?tab=participants`);
			}
			setters.setModal(null);
			surveyParticipantsSetters.setSurveyAccess("selected-employees");
		} else {
			setters.setModal(SURVEY_DETAILS_MODALS.SELECT_ALL_EMPLOYEES_MODAL);
		}
	};

	const handleClose = () => {
		setters.setModal(null);
		setters.setValidQuestions([]);
		setters.setValidName("");
		setters.setStartDate("");
		setters.setEndDate("");
		setters.setPublishFormErrors({ startDate: null, endDate: null });
		setOriginalValues(null);
	};

	const handleCheckboxClick = (options: { label: string; value: string }[]) => {
		const scheduleDates = options.find((option) => option.value === "schedule-dates") !== undefined;
		setScheduleDates(scheduleDates);
	};

	const handleClickSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		const publishUpdates: { startDate?: Date; endDate?: Date | null; scheduleDates?: boolean } = {};

		if (!hasScheduleChanges) {
			handleSurveyAccessChange();
		} else if (!scheduleDates) {
			publishUpdates.endDate = null;
			publishUpdates.scheduleDates = false;
			if (startDate > new Date()) {
				// if the start date is in the future, set it to the current date
				publishUpdates.startDate = new Date();
			}
			publishSurvey(publishUpdates);
		} else {
			publishUpdates.scheduleDates = true;
			if (startDate !== "") {
				if (survey?.startDate !== startDate.toISOString()) {
					publishUpdates.startDate = startDate;
				}
			}
			if (endDate !== "") {
				if (survey?.endDate !== endDate.toISOString()) {
					publishUpdates.endDate = endDate;
				}
			} else {
				publishUpdates.endDate = null;
			}
			actions.validateSurveyDates(() => {
				publishSurvey(publishUpdates);
			});
		}
	};

	const hasScheduleChanges = useMemo(() => {
		if (!originalValues) return false;

		// Helper function to compare dates properly
		const compareDates = (date1: Date | string, date2: Date | string) => {
			if (date1 === "" && date2 === "") return true;
			if (date1 === "" || date2 === "") return false;
			return new Date(date1).getTime() === new Date(date2).getTime();
		};

		return (
			!compareDates(startDate, originalValues.startDate) ||
			!compareDates(endDate, originalValues.endDate) ||
			scheduleDates !== originalValues.scheduleDates
		);
	}, [startDate, endDate, scheduleDates, originalValues]);

	const hasSurveyAccessChanges = useMemo(() => {
		return selectedSurveyAccess !== originalValues?.surveyAccess;
	}, [selectedSurveyAccess, originalValues]);

	const startDateDisabled = surveyStatus === "active";

	const [ref] = useAutoAnimate();

	return (
		<FormModal
			size="extralarge"
			isOpen={modal === SURVEY_DETAILS_MODALS.SURVEY_SETTINGS}
			title={
				<>
					<Typography size="sub-heading" weight="semi-bold">
						Survey Settings
					</Typography>
					<Typography size="paragraph-2" weight="regular">
						{survey?.name}
					</Typography>
				</>
			}
			handleClose={handleClose}
			buttons={[
				// class hacks put the button in the right place
				<div key="save-changes-button" className="z-[2004] mb-[-16px] flex w-full justify-end gap-2">
					{hasScheduleChanges ||
						(hasSurveyAccessChanges && (
							<span className="w-[90px]">
								<ButtonStandard
									size="large"
									type="button"
									color="neutral"
									variant="outline"
									onClick={() => {
										if (originalValues) {
											setScheduleDates(originalValues.scheduleDates);
											setters.setStartDate(
												originalValues.startDate ? new Date(originalValues.startDate) : ""
											);
											setters.setEndDate(
												originalValues.endDate ? new Date(originalValues.endDate) : ""
											);
											setters.setPublishFormErrors({ startDate: null, endDate: null });
											setSelectedSurveyAccess(originalValues.surveyAccess);
											surveyParticipantsSetters.setSurveyAccess(originalValues.surveyAccess);
										}
									}}
								>
									Discard
								</ButtonStandard>
							</span>
						))}
					<span className="w-32">
						<ButtonStandard
							size="large"
							type="submit"
							color="green"
							disabled={
								surveyStatus === "closed" ||
								(!hasScheduleChanges && !hasSurveyAccessChanges) ||
								Object.values(publishFormErrors).some((error) => error !== null)
							}
						>
							Save Changes
						</ButtonStandard>
					</span>
				</div>,
			]}
			onSubmit={handleClickSubmit}
		>
			<Spacer height={2} />
			<Grid cols={2}>
				<GridCol>
					<div>
						<Typography size="paragraph-2" color="neutral-9" weight="semi-bold">
							Survey Schedule
						</Typography>
						<Spacer height={1} />
						<Typography size="paragraph-3" color="neutral-9" weight="regular">
							Schedule the survey to control when participants can access it
						</Typography>
					</div>
				</GridCol>
				<GridCol>
					<div ref={ref} className="flex flex-col gap-2">
						<Checkboxes
							options={[
								{
									label: "Schedule Start and End Date",
									value: "schedule-dates",
								},
							]}
							value={scheduleDates ? [{ label: "schedule-dates", value: "schedule-dates" }] : []}
							disabled={(survey?.scheduleDates ?? survey?.startDate) ? ["schedule-dates"] : []}
							onChange={handleCheckboxClick}
						/>
						{scheduleDates ? (
							<>
								<Spacer height={1} />
								<div className="z-[2006] flex flex-col gap-4">
									<DateWithErrors
										disabled={startDateDisabled || surveyStatus === "closed"}
										value={startDate}
										setValue={(date) => {
											setters.setPublishFormErrors({
												...publishFormErrors,
												startDate: null,
											});
											setters.setStartDate(date ?? "");
										}}
										label="Start Date"
										errorMessage={publishFormErrors["startDate"]}
									/>
									<DateWithErrors
										disabled={surveyStatus === "closed"}
										value={endDate}
										setValue={(date) => {
											setters.setPublishFormErrors({
												...publishFormErrors,
												endDate: null,
											});
											setters.setEndDate(date ?? "");
										}}
										label="End Date"
										errorMessage={publishFormErrors["endDate"]}
									/>
								</div>
							</>
						) : null}
					</div>
				</GridCol>
			</Grid>
			<Spacer height={2} />
			<div className="w-full border-b-[1px] border-solid border-neutral-2"></div>
			<Spacer height={2} />
			<Grid cols={2}>
				<GridCol>
					<div>
						<Typography size="paragraph-2" color="neutral-9" weight="semi-bold">
							Survey Access
						</Typography>
						<Spacer height={1} />
						<Typography size="paragraph-3" color="neutral-9" weight="regular">
							Adjust the access to control who can participate in the survey
						</Typography>
					</div>
				</GridCol>
				<GridCol className="z-[2005]">
					<SelectInput
						results={[
							{
								label: "All employees have access",
								value: "all-employees",
							},
							{
								label: "Only selected employees have access",
								value: "selected-employees",
							},
						]}
						value={selectedSurveyAccess}
						onChange={({ value }: { value: "all-employees" | "selected-employees"; label: string }) => {
							setSelectedSurveyAccess(value);
						}}
						disabled={surveyStatus === "closed"}
						isSearchable={false}
						label="Survey Access"
						placeholder="Select Survey Access"
					/>
				</GridCol>
			</Grid>
		</FormModal>
	);
};
